import { fetchAuthSession, signIn, signOut, signUp } from 'aws-amplify/auth';
import { useApiState } from 'hooks/useApiState';
import { useCallback } from 'react';
import { useRecoilState } from 'recoil';
import { AcctState, AcctStateType } from 'stores/atoms/acctState';
import { ModalConfirmState } from 'stores/atoms/modalConfirmState';
import { NoticeMessageState } from 'stores/atoms/noticeMessageState';

/**
 *　アプリケーション共通カスタムフック
 */
export const useAppState = () => {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const [acctState, setAcctState] = useRecoilState(AcctState);
  const [, setNoticeMessageState] = useRecoilState(NoticeMessageState);
  const [, setModalConfirmState] = useRecoilState(ModalConfirmState);
  const { postAcctStateRef, postAcctVerify } = useApiState();

  const acctStateDefault: AcctStateType = {
    user: null,
    company: null,
    usage: null,
    activeContent: null,
    initialized: false,
    proxyLogin: false,
    proxyCompanyCd: null,
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  const _signIn = async (mailAddress: string): Promise<void> => {
    await signIn({
      username: mailAddress,
      options: {
        authFlowType: 'CUSTOM_WITHOUT_SRP',
      },
    });
  };

  /* ----------------------------------------------------------------------- */
  /* パブリック関数定義 */
  /* ----------------------------------------------------------------------- */
  return {
    /**
     * Cognitoのサインイン状態を取得します。
     */
    isSignedIn: useCallback(async (): Promise<boolean> => {
      return (await fetchAuthSession()).identityId ? true : false;
    }, []),

    /**
     * サインアップリクエストをCognitoに送信し、認証コードを受信します。
     * 認証済の場合はサインアウトしてから再度サインアップします。
     */
    signUpRequest: useCallback(async (mailAddress: string) => {
      try {
        // 現在のサインイン状態を破棄
        if ((await fetchAuthSession()).identityId) {
          await signOut();
          setAcctState(acctStateDefault);
        }
        // アカウントの発行
        await signUp({
          username: mailAddress,
          password: 'dummypassword',
          options: {
            userAttributes: {
              email: mailAddress,
            },
          },
        });

        // 認証コードの送信
        await _signIn(mailAddress);
      } catch (e) {
        if (e.name === 'UsernameExistsException') {
          await _signIn(mailAddress);
        } else {
          throw e;
        }
      }
    }, []),

    /**
     * サインインリクエストをCognitoに送信し、認証コードを受信します。
     * 存在しないアカウントの場合はfalseを返却します。
     */
    signInRequest: useCallback(async (mailAddress: string): Promise<boolean> => {
      const response = await postAcctVerify({ mailAddress });

      if (!response.valid) {
        return false;
      }
      await _signIn(mailAddress);

      return true;
    }, []),

    /**
     * サインアウトします。
     */
    signOutRequest: useCallback(async () => {
      try {
        if ((await fetchAuthSession()).identityId) {
          await signOut();
        }
        setAcctState(acctStateDefault);
        return acctStateDefault;
      } catch (e) {
        return acctState;
      }
    }, []),

    /**
     * アカウント関連の状態管理を最新化します。
     */
    initializeAcctState: useCallback(async (companyCd: string | null = null) => {
      const proxyLogin = acctState.proxyLogin;
      const proxyCompanyCd = acctState.proxyCompanyCd;
      let retAcctState: AcctStateType = {
        ...acctStateDefault,
        initialized: true,
      };
      try {
        const session = await fetchAuthSession();
        if (session.identityId) {
          const response = await postAcctStateRef({
            target: ['user', 'company', 'usage', 'activeContent'],
            companyCd: companyCd,
            proxyLogin: proxyLogin,
          });
          retAcctState = {
            ...response,
            initialized: true,
            proxyLogin: proxyLogin,
            proxyCompanyCd: proxyCompanyCd,
          };
        }
      } finally {
        setAcctState(retAcctState);
        return retAcctState;
      }
    }, []),
    initializeAcctStateByProxy: useCallback(async (companyCd: string | null = null) => {
      let retAcctState: AcctStateType = {
        ...acctStateDefault,
        initialized: true,
      };
      try {
        const session = await fetchAuthSession();
        if (session.identityId) {
          const response = await postAcctStateRef({
            target: ['user', 'company', 'usage', 'activeContent'],
            companyCd: companyCd,
            proxyLogin: true,
          });
          retAcctState = {
            ...response,
            initialized: true,
            proxyLogin: true,
            proxyCompanyCd: companyCd,
          };
        }
      } finally {
        setAcctState(retAcctState);
        return retAcctState;
      }
    }, []),

    /**
     * アカウント関連の状態管理を更新します。
     */
    updateAcctState: useCallback(
      async (
        target: ('user' | 'company' | 'usage' | 'activeContent')[],
        companyCd: string | null = null
      ) => {
        const response = await postAcctStateRef({
          target: target,
          companyCd: companyCd,
          proxyLogin: acctState.proxyLogin,
        });

        const updatedAcctState = { ...acctState };

        if (target.includes('user')) {
          updatedAcctState.user = response.user;
        }
        if (target.includes('company')) {
          updatedAcctState.company = response.company;
        }
        if (target.includes('usage')) {
          updatedAcctState.usage = response.usage;
        }
        if (target.includes('activeContent')) {
          updatedAcctState.activeContent = response.activeContent;
        }

        setAcctState(updatedAcctState);
        return updatedAcctState;
      },
      []
    ),

    /**
     * 処理完了メッセージを設定します。
     */
    noticeMessageSuccess: useCallback((message: string): void => {
      setNoticeMessageState({ message: message, status: 'success' });
    }, []),

    /**
     * 異常終了メッセージを設定します。
     */
    noticeMessageFailure: useCallback((message: string): void => {
      setNoticeMessageState({ message: message, status: 'failure' });
    }, []),

    /**
     * 確認ダイアログを表示します。
     */
    confirm: useCallback(
      (props: { title: string; message: string; onSubmit: () => void }): void => {
        setModalConfirmState({
          open: true,
          title: props.title,
          message: props.message,
          onSubmit: props.onSubmit,
        });
      },
      []
    ),
  } as const;
};
